/**
 * 공급계약서 PDF
 * - html2canvas + jsPDF로 실제 파일 생성 (about:blank 인쇄 머리글 없음)
 * - 청크(본문 / 계약당사자 / 별첨별) 단위로 렌더해 별첨은 항상 새 페이지에서 시작
 * - 페이지 높이로 잘라 빈 페이지·중간 시작을 막는다
 */
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import {
  parseContractBodySegments,
  parseContractSections,
} from '@/utils/parseContractSections'

export type ContractPdfParty = {
  storeName?: string
  ownerName?: string
  bizNumber?: string
  address?: string
  addressDetail?: string
}

export type ContractPdfOptions = {
  title: string
  version?: string
  effective?: string
  body: string
  party?: ContractPdfParty
  signature?: string
  signedAt?: string
  signerName?: string
  /** 전자계약 체크 표시. 미전달 시 서명 있으면 true */
  electronicMethod?: boolean
  /** @deprecated 무시됨 */
  autoPrint?: boolean
}

const PARTY_A = {
  name: '주식회사 함우리',
  owner: '김승식',
  bizNumber: '185-81-02287',
  address: '전북특별자치도 전주시 덕진구 기지로 77, 202동 2803호',
}

/** 렌더 폭(px). A4 여백 고려해 약간 좁혀 우측 잘림을 막는다 */
const RENDER_WIDTH = 680
const MARGIN_X_MM = 12
const MARGIN_TOP_MM = 12
const MARGIN_BOTTOM_MM = 14
/** 판면·측정 여유. 실제 캡처가 측정값보다 커지는 경우를 흡수한다 */
const PAGE_HEIGHT_SAFE_MM = 8
/** 페이지에 블록을 넣을 때 maxCssH 대비 사용률(측정 오차 버퍼) */
const PACK_RATIO = 0.88

function escapeHtml(value: string) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function partyAddress(party?: ContractPdfParty) {
  if (!party?.address) return ''
  return [party.address, party.addressDetail].filter(Boolean).join(' ')
}

function signedMeta(opts: ContractPdfOptions) {
  if (!opts.signedAt && !opts.signerName) return ''
  return [opts.signedAt, opts.signerName].filter(Boolean).join(' · ')
}

function segmentsToHtml(body: string) {
  return parseContractBodySegments(body)
    .map((seg) => {
      if (seg.type === 'prose') {
        return `<div class="prose">${escapeHtml(seg.text)}</div>`
      }
      const head = `<tr>${seg.headers.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}</tr>`
      const rows = seg.rows
        .map((row) => `<tr>${row.map((c) => `<td>${escapeHtml(c || '')}</td>`).join('')}</tr>`)
        .join('')
      return `<div class="tw"><table>${head}${rows}</table></div>`
    })
    .join('')
}

function contractPdfStyles() {
  return `
.cpdf{
  width:${RENDER_WIDTH}px;
  margin:0;
  /* html2canvas 하단 글리프 잘림 방지용 여유 */
  padding:0 0 28px;
  color:#15202b;background:#fff;
  font-family:"Pretendard","Apple SD Gothic Neo","Malgun Gothic",sans-serif;
  font-size:12px;line-height:1.7;text-align:left;
  overflow:visible;
}
.cpdf *{box-sizing:border-box;max-width:100%}
.cover{margin:0 0 16px;padding:0 0 12px;border-bottom:2px solid #1f3a56}
.cover__brand{margin:0 0 6px;font-size:10px;font-weight:800;letter-spacing:.12em;color:#5b6b7c}
.cover h1{
  margin:0 0 10px;font-size:20px;font-weight:800;letter-spacing:-.03em;line-height:1.35;color:#102033;
}
.cover__meta{display:block;margin:0;padding:0;list-style:none;font-size:0}
.cover__meta li{
  display:inline-block;margin:0 6px 0 0;padding:0;vertical-align:middle;
  border:1px solid #d5dde7;border-radius:999px;background:#f4f7fa;
  overflow:hidden;
}
/* html2canvas는 flex 수직중앙을 자주 깨뜨리므로 line-height로 맞춘다 */
.cover__meta li span{
  display:block;height:22px;padding:0 10px;
  font-size:10px;font-weight:700;line-height:22px;
  color:#3d4f66;white-space:nowrap;
}
.lead{
  margin:0 0 16px;padding:10px 12px;border-left:3px solid #1f3a56;background:#f5f8fb;
  color:#3d4f66;font-size:11.5px;line-height:1.65;white-space:pre-wrap;word-break:keep-all;overflow-wrap:anywhere;
}
.sec{margin:0}
.sec__label{margin:0 0 2px;font-size:9px;font-weight:800;letter-spacing:.12em;color:#6b7c8f}
.sec h2{
  margin:0 0 12px;padding:0 0 8px;font-size:15px;font-weight:800;letter-spacing:-.02em;
  border-bottom:1px solid #d5dde7;color:#102033;
}
/* 조문 사이에 여백을 둬 페이지 컷이 글자 한가운데가 아니라 조문 사이에서 나가게 한다 */
.article{margin:0 0 14px;padding:0 0 12px;border-bottom:1px solid #e8eef4}
.article:last-child{border-bottom:0;margin-bottom:0;padding-bottom:0}
.article h3{margin:0 0 6px;font-size:12.5px;font-weight:800;color:#1f3a56;letter-spacing:-.01em}
.annex{margin:0}
.prose{
  margin:0;white-space:pre-wrap;word-break:keep-all;overflow-wrap:anywhere;
  font:inherit;font-size:11.5px;line-height:1.7;color:#243141;overflow:visible;
}
.tw{margin:8px 0 0;overflow:visible}
table{width:100%;border-collapse:collapse;table-layout:fixed;font-size:10px}
th,td{
  border:1px solid #cfd8e3;padding:6px 7px;vertical-align:top;text-align:left;
  word-break:keep-all;overflow-wrap:anywhere;
}
th{background:#eef3f8;font-weight:800;color:#1f3a56}
.party-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:0 0 12px}
.party-card{border:1px solid #d5dde7;border-radius:8px;padding:12px;background:#f8fafc}
.party-card--eul{background:#fff;border-color:#1f3a56}
/* html2canvas는 flex 수직정렬이 자주 깨지므로 table+vertical-align으로 맞춘다 */
.party-head{margin:0 0 10px;padding:0}
.party-head-table{
  width:auto;max-width:100%;border-collapse:collapse;border:0;margin:0;padding:0;
  table-layout:auto;background:transparent;
}
.party-head-table td{
  border:0;padding:0;margin:0;vertical-align:middle;background:transparent;
}
.party-tag-cell{width:28px;padding-right:8px !important}
.party-name-cell{text-align:left}
.party-name-cell strong{
  display:block;margin:0;padding:0;
  font-size:13px;font-weight:800;letter-spacing:-.02em;line-height:1.35;color:#15202b;
}
.party-tag{
  display:block;width:24px;height:24px;margin:0;padding:0;
  border-radius:5px;background:#1f3a56;color:#fff;
  font-size:12px;font-weight:800;line-height:24px;text-align:center;
}
.party-tag--eul{background:#0f766e}
.party-card dl{margin:0;display:grid;gap:6px}
.party-card dl > div{display:grid;grid-template-columns:88px 1fr;gap:6px;align-items:start}
.party-card dt{margin:0;font-size:10px;font-weight:700;color:#6b7c8f;line-height:1.4;padding-top:1px}
.party-card dd{margin:0;font-size:11.5px;color:#15202b;word-break:keep-all;overflow-wrap:anywhere}
.meta-grid{display:grid;gap:6px;margin:0 0 10px}
.meta-cell{
  display:grid;grid-template-columns:100px 1fr;gap:8px;align-items:center;
  padding:8px 10px;border:1px solid #e2e8f0;border-radius:6px;background:#fafbfd;
}
.meta-k{font-size:10px;font-weight:800;color:#6b7c8f}
.meta-v{font-size:11.5px;color:#15202b;word-break:keep-all;overflow-wrap:anywhere}
.chk{margin-right:12px;white-space:nowrap}
.chk.is-on{font-weight:800;color:#0f766e}
.sig-block{margin-top:4px;padding:10px 12px;border:1px dashed #9aafc3;border-radius:8px;background:#f7fafc}
.sig-block .meta-k{display:block;margin:0 0 6px}
.sig{
  display:block;max-width:220px;max-height:80px;object-fit:contain;
  background:#fff;border:1px solid #d5dde7;border-radius:5px;padding:5px;
}
`
}

function renderPartyHtml(title: string, opts: ContractPdfOptions, electronic: boolean) {
  const eulAddr = partyAddress(opts.party)
  const meta = signedMeta(opts)
  const eulName = opts.party?.storeName || '을 (판매회사)'
  const eulOwner = opts.party?.ownerName || '—'
  const eulBiz = opts.party?.bizNumber || '—'
  const eulAddress = eulAddr || '— (온보딩·업체정보 입력 후 표시)'

  return `<section class="sec">
    <div class="sec__label">계약당사자</div>
    <h2>${escapeHtml(title)}</h2>
    <div class="party-grid">
      <article class="party-card">
        <div class="party-head">
          <table class="party-head-table" role="presentation"><tr>
            <td class="party-tag-cell"><span class="party-tag">갑</span></td>
            <td class="party-name-cell"><strong>${escapeHtml(PARTY_A.name)}</strong></td>
          </tr></table>
        </div>
        <dl>
          <div><dt>대표자</dt><dd>${escapeHtml(PARTY_A.owner)}</dd></div>
          <div><dt>사업자등록번호</dt><dd>${escapeHtml(PARTY_A.bizNumber)}</dd></div>
          <div><dt>주소</dt><dd>${escapeHtml(PARTY_A.address)}</dd></div>
        </dl>
      </article>
      <article class="party-card party-card--eul">
        <div class="party-head">
          <table class="party-head-table" role="presentation"><tr>
            <td class="party-tag-cell"><span class="party-tag party-tag--eul">을</span></td>
            <td class="party-name-cell"><strong>${escapeHtml(eulName)}</strong></td>
          </tr></table>
        </div>
        <dl>
          <div><dt>대표자</dt><dd>${escapeHtml(eulOwner)}</dd></div>
          <div><dt>사업자등록번호</dt><dd>${escapeHtml(eulBiz)}</dd></div>
          <div><dt>주소</dt><dd>${escapeHtml(eulAddress)}</dd></div>
        </dl>
      </article>
    </div>
    <div class="meta-grid">
      <div class="meta-cell">
        <span class="meta-k">계약방식</span>
        <span class="meta-v">
          <span class="chk">☐ 서면계약</span>
          <span class="chk ${electronic ? 'is-on' : ''}">${electronic ? '☑' : '☐'} 카이온 전자약관·전자계약</span>
        </span>
      </div>
      <div class="meta-cell">
        <span class="meta-k">계약 버전</span>
        <span class="meta-v">${escapeHtml(opts.version || '[시스템 자동표시]')}</span>
      </div>
      <div class="meta-cell">
        <span class="meta-k">체결일·동의자</span>
        <span class="meta-v">${escapeHtml(meta || '[시스템 자동기록 / 성명·직책·본인확인]')}</span>
      </div>
    </div>
    ${
      opts.signature
        ? `<div class="sig-block">
            <span class="meta-k">을 서명</span>
            <img class="sig" src="${escapeHtml(opts.signature)}" alt="서명" />
          </div>`
        : ''
    }
  </section>`
}

function buildCoverHtml(opts: ContractPdfOptions, electronic: boolean) {
  const metaChips = [
    opts.version ? `v${opts.version}` : '',
    opts.effective ? `시행 ${opts.effective}` : '',
    electronic ? '전자계약' : '',
    opts.signedAt ? `체결 ${opts.signedAt}` : '',
  ].filter(Boolean)

  return `<header class="cover">
    <p class="cover__brand">CAION · 함우리</p>
    <h1>${escapeHtml(opts.title || '공동구매 상품공급 및 판매운영 기본계약서')}</h1>
    ${
      metaChips.length
        ? `<ul class="cover__meta">${metaChips
            .map((c) => `<li><span>${escapeHtml(c)}</span></li>`)
            .join('')}</ul>`
        : ''
    }
  </header>`
}

/** 청크: 본문(표지+조문) → 계약당사자 → 별첨 각 1개. 청크 경계 = 새 페이지 */
function buildPdfChunks(opts: ContractPdfOptions): string[] {
  const groups = parseContractSections(opts.body || '')
  const intro = groups.find((g) => g.kind === 'intro')
  const introText = intro?.blocks[0]?.body || ''
  const electronic =
    opts.electronicMethod !== undefined
      ? opts.electronicMethod
      : Boolean(opts.signature || opts.signedAt)

  const chunks: string[] = []

  const basic = groups.find((g) => g.kind === 'basic')
  const articles = (basic?.blocks || [])
    .map(
      (block) =>
        `<article class="article"><h3>${escapeHtml(block.title)}</h3>${segmentsToHtml(block.body)}</article>`,
    )
    .join('')

  chunks.push(`
    ${buildCoverHtml(opts, electronic)}
    ${introText ? `<p class="lead">${escapeHtml(introText)}</p>` : ''}
    <section class="sec">
      <div class="sec__label">기본계약</div>
      <h2>${escapeHtml(basic?.title || '기본계약 (제1조 ~ 제16조)')}</h2>
      ${articles}
    </section>
  `)

  const party = groups.find((g) => g.kind === 'party')
  if (party) {
    chunks.push(renderPartyHtml(party.title, opts, electronic))
  }

  for (const group of groups) {
    if (group.kind !== 'annex') continue
    for (const block of group.blocks) {
      chunks.push(`
        <section class="sec">
          <div class="sec__label">별첨</div>
          <h2>${escapeHtml(block.title)}</h2>
          <div class="annex">${segmentsToHtml(block.body)}</div>
        </section>
      `)
    }
  }

  return chunks
}

function pdfFilename(opts: ContractPdfOptions) {
  const base = (opts.title || '공급계약서')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 60)
  const ver = opts.version ? `_v${opts.version}` : ''
  return `${base}${ver}.pdf`
}

function mountChunk(html: string): HTMLElement {
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  /* 화면 밖 음수 좌표는 html2canvas 하단 잘림을 유발할 수 있어 뷰포트 안에서 숨긴다 */
  host.style.cssText = `position:fixed;left:0;top:0;width:${RENDER_WIDTH}px;background:#fff;opacity:0;pointer-events:none;z-index:-1;`
  host.innerHTML = `<style>${contractPdfStyles()}</style><div class="cpdf">${html}</div>`
  document.body.appendChild(host)
  return host
}

async function waitImages(root: HTMLElement) {
  const images = Array.from(root.querySelectorAll('img'))
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve()
            return
          }
          img.onload = () => resolve()
          img.onerror = () => resolve()
        }),
    ),
  )
}

function pdfPageMetrics(pdf: jsPDF) {
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const contentW = pageW - MARGIN_X_MM * 2
  const contentH = pageH - MARGIN_TOP_MM - MARGIN_BOTTOM_MM - PAGE_HEIGHT_SAFE_MM
  /** RENDER_WIDTH 기준, 한 페이지에 들어갈 최대 CSS 높이 */
  const maxCssH = (contentH * RENDER_WIDTH) / contentW
  return { contentW, contentH, maxCssH }
}

/** 페이지 단위로 통째 넘길 블록(조문·표 행 등). 표는 행 단위로 나눈다. */
function collectPageBlocks(root: HTMLElement): HTMLElement[] {
  const raw = Array.from(
    root.querySelectorAll(
      [
        '.cover',
        '.lead',
        '.sec__label',
        '.sec > h2',
        '.article',
        '.party-grid',
        '.meta-cell',
        '.sig-block',
        '.annex > .prose',
        '.annex > .tw',
      ].join(','),
    ),
  ) as HTMLElement[]

  const out: HTMLElement[] = []
  for (const el of raw) {
    if (el.classList.contains('tw')) {
      const rows = Array.from(el.querySelectorAll('tr')) as HTMLElement[]
      if (rows.length > 1) {
        out.push(...rows)
        continue
      }
    }
    out.push(el)
  }
  return out
}

function packBlocksIntoPages(blocks: HTMLElement[], maxCssH: number): number[][] {
  const limit = maxCssH * PACK_RATIO
  const heights = blocks.map((el) => el.offsetHeight || el.getBoundingClientRect().height)
  const pages: number[][] = []
  let current: number[] = []
  let used = 0

  blocks.forEach((_, index) => {
    const h = heights[index]
    const gap = current.length ? 8 : 0
    if (current.length && used + gap + h > limit) {
      pages.push(current)
      current = []
      used = 0
    }
    const addGap = current.length ? 8 : 0
    current.push(index)
    used += addGap + h
  })
  if (current.length) pages.push(current)
  return pages
}

/** 원본 청크를 복제해 해당 페이지 블록만 남긴다 */
function buildPageElement(root: HTMLElement, keepIndexes: number[]): HTMLElement {
  const clone = root.cloneNode(true) as HTMLElement
  const cloneBlocks = collectPageBlocks(clone)
  const keep = new Set(keepIndexes)

  cloneBlocks.forEach((el, index) => {
    if (!keep.has(index)) el.remove()
  })

  let changed = true
  while (changed) {
    changed = false
    clone.querySelectorAll('.annex, .meta-grid, .sec, table, thead, tbody, .tw').forEach((node) => {
      const el = node as HTMLElement
      const empty =
        !el.querySelector(
          'tr, .article, .prose, .meta-cell, .party-grid, .sig-block, h2, .sec__label, .lead, .cover, img',
        ) && !el.textContent?.trim()
      if (empty || (el.matches('table, thead, tbody, .tw') && !el.querySelector('tr'))) {
        el.remove()
        changed = true
      }
    })
  }

  return clone
}

async function captureElement(el: HTMLElement): Promise<HTMLCanvasElement> {
  await waitImages(el)
  /* 레이아웃 확정 후 한 프레임 대기 */
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  const h = Math.ceil(el.scrollHeight + 4)
  return html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    width: RENDER_WIDTH,
    height: h,
    windowWidth: RENDER_WIDTH,
    windowHeight: h,
    scrollX: 0,
    scrollY: -window.scrollY,
    x: 0,
    y: 0,
  })
}

function addFullCanvasPage(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  contentW: number,
  contentH: number,
  startNewPage: boolean,
) {
  const drawW = contentW
  const drawH = (canvas.height * contentW) / canvas.width
  /*
   * 판면을 넘기면 축소하지 않는다. 빈 페이지를 만들지 않도록
   * addPage 전에 높이를 검사한다.
   */
  if (drawH > contentH + 0.2) {
    return false
  }
  if (startNewPage) pdf.addPage()
  const data = canvas.toDataURL('image/jpeg', 0.98)
  pdf.addImage(data, 'JPEG', MARGIN_X_MM, MARGIN_TOP_MM, drawW, drawH)
  return true
}

/**
 * 조문 단위로 페이지를 나눈 뒤, 실제 렌더 높이로 한 번 더 검증한다.
 * 넘치면 마지막 조문을 다음 페이지로 밀어 글자 잘림을 없앤다.
 */
async function addChunkToPdf(pdf: jsPDF, root: HTMLElement, startNewPage: boolean) {
  const { contentW, contentH, maxCssH } = pdfPageMetrics(pdf)
  const blockCount = collectPageBlocks(root).length
  if (!blockCount) {
    const canvas = await captureElement(root)
    addFullCanvasPage(pdf, canvas, contentW, contentH, startNewPage)
    return
  }

  let pages = packBlocksIntoPages(collectPageBlocks(root), maxCssH)
  const pageHost = document.createElement('div')
  pageHost.setAttribute('aria-hidden', 'true')
  pageHost.style.cssText = `position:fixed;left:0;top:0;width:${RENDER_WIDTH}px;background:#fff;opacity:0;pointer-events:none;z-index:-1;`
  const styleNode = root.parentElement?.querySelector('style')?.cloneNode(true)
  if (styleNode) pageHost.appendChild(styleNode)
  document.body.appendChild(pageHost)

  try {
    let p = 0
    while (p < pages.length) {
      let indexes = pages[p].slice()
      let fitted = false

      while (indexes.length) {
        const pageEl = buildPageElement(root, indexes)
        pageHost.appendChild(pageEl)
        const canvas = await captureElement(pageEl)
        const needNewPage = startNewPage || p > 0
        const ok = addFullCanvasPage(pdf, canvas, contentW, contentH, needNewPage)
        pageEl.remove()

        if (ok) {
          fitted = true
          startNewPage = true
          break
        }

        /* 판면 초과 → 마지막 블록을 다음 페이지로 */
        if (indexes.length === 1) {
          /* 단일 블록이 한 페이지보다 크면 축소 배치(최후 수단) */
          if (startNewPage || p > 0) pdf.addPage()
          let drawW = contentW
          let drawH = (canvas.height * contentW) / canvas.width
          const scale = contentH / drawH
          drawW *= scale
          drawH = contentH
          pdf.addImage(
            canvas.toDataURL('image/jpeg', 0.98),
            'JPEG',
            MARGIN_X_MM,
            MARGIN_TOP_MM,
            drawW,
            drawH,
          )
          fitted = true
          startNewPage = true
          break
        }

        const moved = indexes.pop() as number
        if (!pages[p + 1]) pages.push([])
        pages[p + 1].unshift(moved)
        pages[p] = indexes.slice()
      }

      p += 1
    }
  } finally {
    pageHost.remove()
  }
}

/** 모든 페이지 하단에 `현재 / 전체` 페이지 번호를 찍는다 */
function stampPageNumbers(pdf: jsPDF) {
  const total = pdf.getNumberOfPages()
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()

  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i)
    pdf.setDrawColor(200, 208, 216)
    pdf.setLineWidth(0.2)
    pdf.line(MARGIN_X_MM, pageH - 11, pageW - MARGIN_X_MM, pageH - 11)

    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(90, 100, 112)
    pdf.text(`${i} / ${total}`, pageW / 2, pageH - 7, { align: 'center' })
  }
}

/** 계약서 jsPDF 문서를 만든다 (저장·업로드 공용). */
async function buildContractPdfDocument(opts: ContractPdfOptions): Promise<jsPDF> {
  const chunks = buildPdfChunks(opts)
  if (!chunks.length) {
    throw new Error('계약서 본문이 없습니다.')
  }

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const hosts: HTMLElement[] = []

  try {
    for (let i = 0; i < chunks.length; i += 1) {
      const host = mountChunk(chunks[i])
      hosts.push(host)
      const el = host.querySelector('.cpdf') as HTMLElement
      await waitImages(el)
      await addChunkToPdf(pdf, el, i > 0)
    }
    stampPageNumbers(pdf)
    return pdf
  } finally {
    hosts.forEach((host) => host.remove())
  }
}

/** 온보딩 제출용 — 체결 스냅샷 PDF Blob */
export async function buildContractPdfBlob(opts: ContractPdfOptions): Promise<Blob> {
  const pdf = await buildContractPdfDocument(opts)
  return pdf.output('blob')
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  const chunk = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

/** 계약서 PDF 파일을 바로 내려받는다. */
export async function downloadContractPdf(opts: ContractPdfOptions): Promise<void> {
  const pdf = await buildContractPdfDocument(opts)
  pdf.save(pdfFilename(opts))
}

/** @deprecated downloadContractPdf 사용 */
export async function openContractPdf(opts: ContractPdfOptions): Promise<boolean> {
  try {
    await downloadContractPdf(opts)
    return true
  } catch (error) {
    console.error(error)
    return false
  }
}

export const CONTRACT_PDF_MESSAGE = 'jangi-download-contract-pdf'
